import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SdiFile, styles } from '@sdir/sds';
import { useIntl } from 'react-intl';
import { AttachmentResult } from '@sdir/httpclient/lib/clients/aps/application';
import { isAttachmentResult } from '../../../types';

interface FileButtonProps {
  onClick: () => void;
  file: File | AttachmentResult;
  className?: string;
  hoverIcon?: any;
  showSize?: boolean;
}

const sizeUnits = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
const unitCap = 1024;
const calculateSize: (file: File) => { sizeNumber: number; sizeUnit: string } = (file: File) => {
  let unitIndex = 0;
  let sizeNumber = file.size;
  while (sizeNumber >= unitCap && unitIndex + 1 < sizeUnits.length) {
    unitIndex += 1;
    sizeNumber /= unitCap;
  }
  return { sizeNumber, sizeUnit: sizeUnits[unitIndex] };
};

export const FileButton: React.FC<FileButtonProps> = ({
  onClick,
  file,
  className,
  hoverIcon,
  showSize = false,
}) => {
  const intl = useIntl();
  const sizeInfo = useMemo(() => {
    if (!showSize) return '';
    if (file && isAttachmentResult(file)) return '';
    const size = calculateSize(file);
    return `(${intl.formatNumber(size.sizeNumber, { maximumFractionDigits: 2 })} ${size.sizeUnit})`;
  }, [showSize, file, intl]);
  const name = isAttachmentResult(file) ? file.blobName : file.name;
  return (
    <Button className={className} type="button" onClick={onClick}>
      <div className="file-btn-icon">
        <SdiFile size="s" height="1em" color={styles.colors.primary} />
      </div>
      <div className="file-btn-text">
        <span className="file-name">{name}</span>
        {sizeInfo && <span className="size-info">{sizeInfo}</span>}
      </div>
      {hoverIcon && <div className="file-btn-hover">{hoverIcon}</div>}
    </Button>
  );
};

const Button = styled.button`
  display: flex;
  align-items: center;
  border: none;
  box-shadow: none;
  border-radius: 0.5rem;
  background: #e5ebf080;
  color: ${styles.colors.primary};
  padding: 0.5rem 2rem;
  min-height: 6rem;
  text-align: start;

  .file-btn-icon,
  .file-btn-edit {
    flex: 0 0;
    line-height: 1em;
  }

  .file-btn-text {
    flex: 1 1;
    padding: 0 3rem 0 1.5rem;
    font-family: ${styles.fonts.stfBook};
    display: flex;
    column-gap: 2rem;
    flex-wrap: wrap;
    line-height: 1em;

    .file-name {
      flex-shrink: 1;
      word-break: break-word;
    }

    .size-info {
      font-family: ${styles.fonts.stfBookItalic};
    }
  }

  .file-btn-hover {
    visibility: hidden;
  }

  &:hover,
  &:focus {
    background: ${styles.colors.primary10};

    .file-btn-hover {
      visibility: visible;
    }
  }
`;
